import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonSize,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
} from "@/mds";

const CREATE_EXAMPLES = [
  "People I know, how we met, and the last time we talked",
  "Recipes I like, their ingredients, cuisines, and occasions",
  "Startup ideas, target users, pain points, and open questions",
  "Trips, destinations, dates, reservations, and people joining",
];

export const MemoryAgentDialog = ({
  isOpen,
  mode,
  manifestName,
  hasExistingDraft,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  mode: "create" | "customize" | "edit";
  manifestName: string | null;
  hasExistingDraft: boolean;
  onClose: () => void;
  onSubmit: (request: string) => void;
}) => {
  const [request, setRequest] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const dialogContent = memoryAgentDialogContent({ mode, manifestName });

  useEffect(() => {
    if (isOpen) setRequest("");
  }, [isOpen, manifestName, mode]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedRequest = request.trim();
    if (!trimmedRequest) return;
    onSubmit(trimmedRequest);
  };

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        {dialogContent.title}
      </MDSText>
      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <DialogCopy>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
            {dialogContent.description}
          </MDSText>
          <StyledForm ref={formRef} onSubmit={submit}>
            <MDSTextField
              aria-label={dialogContent.inputLabel}
              autoFocus
              isTextArea
              maxLength={4_000}
              placeholder={dialogContent.placeholder}
              textAreaProps={{ rows: 5 }}
              value={request}
              variant={MDSTextFieldVariant.Form}
              onChange={setRequest}
            />
          </StyledForm>
          {mode === "create" ? (
            <ExampleList aria-label="Memory examples">
              {CREATE_EXAMPLES.map(example => (
                <MDSButton
                  key={example}
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onPress={() => setRequest(example)}
                >
                  {example}
                </MDSButton>
              ))}
            </ExampleList>
          ) : null}
          {hasExistingDraft ? (
            <DraftWarning role="status">
              You already have an unsent Mem Agent draft. Continuing will replace it with this
              request.
            </DraftWarning>
          ) : null}
        </DialogCopy>
      </MDSDialogTextContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Transparent} onPress={onClose}>
          Cancel
        </MDSButton>
        <MDSButton
          isDisabled={!request.trim()}
          variant={MDSButtonVariant.Contrast}
          onPress={() => formRef.current?.requestSubmit()}
        >
          {hasExistingDraft ? "Replace draft & continue" : "Continue in Agent"}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
};

const memoryAgentDialogContent = ({
  mode,
  manifestName,
}: {
  mode: "create" | "customize" | "edit";
  manifestName: string | null;
}) => {
  if (mode === "edit" && manifestName) {
    return {
      title: `Change ${manifestName}`,
      description:
        "Describe the types, relationships, filters, or presentation you want changed. Mem Agent will inspect the current definition before editing it.",
      inputLabel: `Changes to ${manifestName}`,
      placeholder: "For example: add a view of urgent requests grouped by product area",
    };
  }

  if (mode === "customize") {
    return {
      title: "Change Memory",
      description:
        "Describe a type, relationship, or view you want added or changed. Mem Agent will inspect all current definitions and preserve everything else.",
      inputLabel: "What should change in Memory?",
      placeholder: "For example: add a Person type shared by my customer and team views",
    };
  }

  return {
    title: "Create a Memory view",
    description:
      "Describe what should stay organized and current. Mem Agent will design the types, relationships, and useful views, then backfill your notes.",
    inputLabel: "What should Memory track?",
    placeholder:
      "For example: restaurants I have visited, favorite dishes, who I went with, and places I want to try",
  };
};

const DialogCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const ExampleList = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  button {
    height: auto;
    justify-content: flex-start;
    text-align: left;
    white-space: normal;
  }
`;

const DraftWarning = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  padding: ${({ theme }) => theme.spacing[3]};
`;
